import React, { useState } from 'react';
import moment from 'moment';
import { COLUMN_TYPES, refillRequestMessageRestriction } from '../../../../config/app.config';
import { useNestedModal } from '../../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';
import { DataTable } from '../../../modals/ModalTables';
import {
  ImageGalleryModal,
  TextDisplayModal,
} from '../../../modals/ContentModals';
import { Button } from 'reactstrap';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import { useRefillRequestModalContext } from '../utils';
import ModalPdfViewer from '../../Common/ModalPdfViewer';
import { buildMessageHistoryTranscript } from '../../../../utils/buildMessageHistoryTranscript';

export function AddressField({ addressData }) {
  const fullAddress = addressData.filter(Boolean).join(', ');

  if (!fullAddress.trim()) return null;

  return (
    <div
      className="address modal-main-info-field"
      style={{ gridArea: 'address' }}
    >
      <p className="field-label">Shipping Address</p>
      <p className="field-data">{fullAddress}</p>
    </div>
  );
}

const getMessageHistoryTableColumns = (messageModal, imagesModal, pdfModal, setIsMessageModalOpen, messages) => {
    // Find completion date (when status changed to "Shipped" or "Ready for pickup")
    const getCompletionDate = () => {
        if (!messages || !Array.isArray(messages)) return null;

        // Look for messages with final statuses
        const completionMessages = messages.filter(message =>
            message.status === 'Shipped' || message.status === 'Ready for pickup'
        );

        if (completionMessages.length === 0) return null;

        // Sort by date and take the latest
        const latestCompletion = completionMessages.sort((a, b) =>
            moment(b.date + ' ' + b.time).diff(moment(a.date + ' ' + a.time))
        )[0];

        return moment(latestCompletion.date + ' ' + latestCompletion.time);
    };

    // Check if more than the allowed days have passed since completion
    const isMoreThanAllowedDaysAfterCompletion = () => {
        const completionDate = getCompletionDate();
        if (!completionDate) return false;

        const currentDate = moment();
        const daysDifference = currentDate.diff(completionDate, 'days');

        return daysDifference > refillRequestMessageRestriction.daysAfterCompletion;
    };

    const isOlderThanAllowedDays = isMoreThanAllowedDaysAfterCompletion();

    return [
          {
            header: 'Date',
            field: 'date',
            type: COLUMN_TYPES.TEXT,
            headerClassName: 'date-column',
          },
          {
            header: 'Time',
            field: 'time',
            type: COLUMN_TYPES.TEXT,
            headerClassName: 'time-column',
          },
          {
            header: 'Status',
            field: 'status',
            type: COLUMN_TYPES.TEXT,
            headerClassName: 'refill-request-status-column',
          },
          {
            header: (
              <div className="message-column-header">
                <span>Messages</span>
                <span className="client-messages-label">Client messages</span>
                  {!isOlderThanAllowedDays && (
                    <Button
                        className="save-update"
                        color="primary"
                        onClick={() => setIsMessageModalOpen(true)}
                    >
                        Create Message
                    </Button>
                  )}
              </div>
            ),
            field: 'message',
            type: COLUMN_TYPES.TRUNCATED_TEXT,
            headerClassName: 'stretched-column',
            maxLines: 10,
            ellipsisRenderer: (message, rowData) => (
              <span>
                ...{' '}
                <button
                  type="button"
                  className="nav-link-style"
                  onClick={() => messageModal.open(rowData)}
                >
                  View
                </button>
              </span>
            ),
          },
          {
            header: 'Images',
            field: 'images',
            type: COLUMN_TYPES.IMAGES,
            headerClassName: 'action-column',
            onImageClick: (images) => imagesModal.open(images),
            buttonText: (count) => `View (${count})`,
          },
          {
            header: 'PDFs',
            field: 'pdfs',
            type: COLUMN_TYPES.PDFS,
            headerClassName: 'action-column',
            onPdfClick: (pdfs) => pdfModal.open(pdfs),
            renderCell: (count) => (count.length ? `View (${count.length})` : '-'),
          },
        ];
};

export function StatusHistoryTable({ messages, header }) {
  const messageModal = useNestedModal();
  const imagesModal = useNestedModal();
  const pdfModal = useNestedModal();
  const { setIsMessageModalOpen } = useRefillRequestModalContext();
  const [copiedHistory, setCopiedHistory] = useState(false);

  const tableColumns = getMessageHistoryTableColumns(
    messageModal,
    imagesModal,
    pdfModal,
    setIsMessageModalOpen,
    messages
  );

  const handleCopyHistory = () => {
    const text = buildMessageHistoryTranscript({
      requestType: 'Refill Request',
      header,
      messages,
    });
    navigator.clipboard.writeText(text).then(() => {
      setCopiedHistory(true);
      setTimeout(() => setCopiedHistory(false), 1500);
    });
  };

  const allImages = (messages || []).flatMap((m) =>
  Array.isArray(m.images) ? m.images : [],
);
const allPdfs = (messages || []).flatMap((m) =>
  Array.isArray(m.pdfs) ? m.pdfs : [],
);

  return (
    <>
      <div className="message-history-table modal-table">
        <table className="message-history-toolbar">
          <tbody>
            <tr>
              <td className="toolbar-title-cell">
                <div className="toolbar-title-row">
                  <h3 className="large">Message History</h3>
                  <Button
                    type="button"
                    className="btn-utility"
                    onClick={handleCopyHistory}
                  >
                    <ContentCopyIcon size={16} aria-hidden />
                    Copy History
                  </Button>
                  {copiedHistory && (
                    <span className="copied-hint">Copied!</span>
                  )}
                </div>
              </td>
              <td className="action-column">
                {allImages.length > 0 && (
                  <button
                    type="button"
                    className="nav-link-style history-attachment-link"
                    onClick={() => imagesModal.open({ images: allImages })}
                  >
                    {`Images (${allImages.length})`}
                  </button>
                )}
              </td>
              <td className="action-column">
                {allPdfs.length > 0 && (
                  <button
                    type="button"
                    className="nav-link-style history-attachment-link"
                    onClick={() => pdfModal.open({ pdfs: allPdfs })}
                  >
                    {`PDFs (${allPdfs.length})`}
                  </button>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="messages-container">
          <DataTable
            columns={tableColumns}
            rows={
              messages.map((m) => ({
                ...m,
                rowCustomClassName:
                  m.authorType === 'client' && 'client-message',
              })) || []
            }
            noDataMessage="No message history available for this refill request."
            noDataColSpan={6}
          />
        </div>
      </div>

      <TextDisplayModal
        modalClassName="centered"
        isOpen={messageModal.isOpen}
        onClose={messageModal.close}
        title="Message"
        content={messageModal?.record?.message || ''}
      />

      <ImageGalleryModal
        modalClassName="centered"
        isOpen={imagesModal.isOpen}
        onClose={imagesModal.close}
        title="Image Viewer"
        images={imagesModal?.record?.images || []}
      />

      <ModalPdfViewer
        isOpen={pdfModal.isOpen}
        dataPdf={pdfModal?.record?.pdfs || []}
        onClose={pdfModal.close}
      />
    </>
  );
}
